import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Quote-recovery flow. A client who didn't create an account during the
// initial submission can still come back here, type their reference + email
// or phone, and be taken to the registration page with their data
// pre-filled. If a real account already exists, we send them to /login.
export default function QuoteRecoveryPage() {
  const navigate = useNavigate();
  const [reference, setReference] = useState("");
  const [contact, setContact] = useState("");
  const [loading, setLoading] = useState(false);
  // Phase 2D — when an auto-created account exists for the recovered request,
  // we no longer dead-end on /login (the client has no usable password). The
  // user now picks between: "I know my password" → /login, or "I never got
  // my credentials" → /register-client (which silently resets the password
  // via backend recovery path).
  const [accountChoice, setAccountChoice] = useState(null); // { email }

  const submit = async (e) => {
    e.preventDefault();
    if (!reference.trim() || !contact.trim()) {
      toast.error("Renseignez la référence et votre email ou téléphone"); return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/quote-recovery`, {
        reference: reference.trim().toUpperCase(),
        email_or_phone: contact.trim(),
      });
      if (data.has_account_already) {
        setAccountChoice({ email: data.client_email || "" });
      } else {
        toast.success("Demande retrouvée — finalisez la création de votre compte.");
        navigate("/register-client", { state: { prefill: data } });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Demande introuvable");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="quote-recovery-page" className="min-h-screen bg-[#050505] flex flex-col">
      <PublicHeader />
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md bg-[#121212] border border-zinc-800 rounded-xl p-6 sm:p-8">
          <div className="flex justify-center mb-4">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/30">
              <MagnifyingGlass size={26} weight="bold" className="text-amber-400" />
            </span>
          </div>
          <h1 className="text-xl sm:text-2xl font-semibold text-white text-center mb-1">Retrouver ma demande</h1>
          <p className="text-sm text-zinc-400 text-center mb-5">
            Saisissez le numéro de devis reçu lors de votre demande, puis l'email ou le téléphone associé.
          </p>

          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Numéro de devis</Label>
              <Input data-testid="recovery-reference" value={reference} onChange={(e) => setReference(e.target.value)}
                placeholder="ex. SD-2026-123" autoCapitalize="characters"
                className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10 uppercase tracking-wider" />
            </div>
            <div className="space-y-1">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email ou téléphone</Label>
              <Input data-testid="recovery-contact" value={contact} onChange={(e) => setContact(e.target.value)}
                placeholder="vous@exemple.fr ou +33 6 XX XX XX XX"
                className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
            </div>

            <Button data-testid="recovery-submit" type="submit" disabled={loading}
              className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 h-11 mt-2">
              {loading ? "Recherche…" : <>Retrouver ma demande <ArrowRight size={16} className="ml-1" /></>}
            </Button>
          </form>

          {accountChoice && (
            <div data-testid="recovery-account-choice" className="mt-5 pt-5 border-t border-zinc-800 space-y-3">
              <p className="text-sm text-zinc-300 text-center">
                Un compte existe déjà pour cette demande.
              </p>
              <div className="grid grid-cols-1 gap-2">
                <Button
                  data-testid="recovery-login"
                  onClick={() => navigate("/login", { state: { prefill_email: accountChoice.email } })}
                  className="w-full bg-zinc-800 text-white hover:bg-zinc-700 border border-zinc-700 h-11"
                >
                  Je connais mon mot de passe — Se connecter
                </Button>
                <Button
                  data-testid="recovery-register"
                  onClick={() => navigate("/register-client", { state: { prefill: { client_email: accountChoice.email } } })}
                  variant="outline"
                  className="w-full border-amber-500/40 text-amber-300 hover:bg-amber-500/10 h-11"
                >
                  Je n'ai jamais reçu mes identifiants — Définir mon mot de passe
                </Button>
              </div>
            </div>
          )}

          <button data-testid="recovery-back" onClick={() => navigate("/")}
            className="text-xs text-zinc-500 hover:text-zinc-300 block mx-auto mt-5">
            ← Retour à l'accueil
          </button>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
